/**
 * Componente EstoqueAlertasTable
 * 
 * Tabela responsiva para exibir alertas de estoque e validade.
 * Em mobile, exibe como cards. Em desktop, exibe como tabela.
 * 
 * @module components/Charts/EstoqueAlertasTable
 */

import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { 
    FaBoxes,
    FaExclamationCircle,
    FaCheckCircle,
    FaPills,
    FaBarcode,
    FaCalendarCheck,
} from 'react-icons/fa';

/**
 * Mapeamento de cor e ícone para o tipo de alerta.
 */
const ALERTA_MAP = {
    ESTOQUE_BAIXO: {
        icon: FaBoxes, 
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        label: 'Estoque Baixo'
    },
    VALIDADE_PROXIMA: {
        icon: FaExclamationCircle, 
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'Validade Próxima'
    },
};

/**
 * Componente de Tabela para exibir alertas de estoque e validade.
 * 
 * @param {Array<Object>} alertas - Lista de alertas de lote.
 * @param {string} [className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de tabela/cards responsivo
 */
const EstoqueAlertasTable = ({ alertas, className = '' }) => {
    if (!alertas || alertas.length === 0) {
        return (
            <div className={`h-40 flex items-center justify-center text-gray-500 text-sm ${className}`} role="status" aria-live="polite">
                <FaCheckCircle className="mr-2 text-green-500" aria-hidden="true" />
                <span>Nenhum alerta de estoque ou validade crítico encontrado.</span>
            </div>
        );
    }

    const formatarData = (data) => moment(data).format('DD/MM/YYYY');

    return (
        <div className={className}>
            {/* Versão Desktop: Tabela */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" role="table" aria-label="Tabela de alertas de estoque">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Alerta
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Medicamento
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                Lote
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Qtd. Disp.
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Vencimento
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {alertas.map((alerta) => {
                            const { icon: Icon, color, label } = ALERTA_MAP[alerta.tipo_alerta] || {};
                            const isValidade = alerta.tipo_alerta === 'VALIDADE_PROXIMA';

                            return (
                                <tr 
                                    key={alerta.lote_id}
                                    className={`transition-colors ${isValidade ? 'bg-red-50/50 hover:bg-red-100' : 'bg-yellow-50/50 hover:bg-yellow-100'}`}
                                    role="row"
                                >
                                    <td className={`px-4 lg:px-6 py-3 whitespace-nowrap text-sm font-medium ${color}`}>
                                        <div className="flex items-center gap-2">
                                            <Icon className="inline" aria-hidden="true" />
                                            <span>{label}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {alerta.medicamento_nome}
                                    </td>
                                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                                        {alerta.numero_lote}
                                    </td>
                                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-700">
                                        {alerta.quantidade_disponivel} {alerta.unidade_medida}
                                    </td>
                                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                        {formatarData(alerta.data_validade)} ({alerta.dias_para_vencer} dias)
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Versão Mobile: Cards */}
            <div className="md:hidden space-y-3" role="list" aria-label="Lista de alertas de estoque">
                {alertas.map((alerta) => {
                    const { icon: Icon, color, bgColor, borderColor, label } = ALERTA_MAP[alerta.tipo_alerta] || {};
                    const isValidade = alerta.tipo_alerta === 'VALIDADE_PROXIMA';

                    return (
                        <div
                            key={alerta.lote_id}
                            className={`bg-white border ${borderColor} rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${bgColor}`}
                            role="listitem"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Icon className={color} size={16} aria-hidden="true" />
                                    <span className={`text-sm font-semibold ${color}`}>
                                        {label}
                                    </span>
                                </div>
                                {alerta.dias_para_vencer !== undefined && (
                                    <div className={`text-xs font-bold px-2 py-1 rounded ${isValidade ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {alerta.dias_para_vencer} dias
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <FaPills className="text-gray-400" size={12} aria-hidden="true" />
                                    <span className="text-sm font-medium text-gray-900">
                                        {alerta.medicamento_nome}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div>
                                        <div className="flex items-center gap-1 text-gray-500 mb-1">
                                            <FaBarcode className="text-gray-400" size={10} aria-hidden="true" />
                                            <span className="font-medium">Lote:</span>
                                        </div>
                                        <p className="text-gray-700 font-medium">
                                            {alerta.numero_lote}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1 text-gray-500 mb-1">
                                            <FaBoxes className="text-gray-400" size={10} aria-hidden="true" />
                                            <span className="font-medium">Qtd.:</span>
                                        </div>
                                        <p className="text-gray-700 font-semibold">
                                            {alerta.quantidade_disponivel} {alerta.unidade_medida}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                                    <FaCalendarCheck className="text-gray-400" size={12} aria-hidden="true" />
                                    <span className="text-xs text-gray-600">
                                        <span className="font-medium">Vencimento:</span>{' '}
                                        {formatarData(alerta.data_validade)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

EstoqueAlertasTable.propTypes = {
    /** Lista de alertas de lote */
    alertas: PropTypes.arrayOf(PropTypes.shape({
        lote_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        tipo_alerta: PropTypes.oneOf(['ESTOQUE_BAIXO', 'VALIDADE_PROXIMA']).isRequired,
        medicamento_nome: PropTypes.string.isRequired,
        numero_lote: PropTypes.string.isRequired,
        quantidade_disponivel: PropTypes.number.isRequired,
        unidade_medida: PropTypes.string.isRequired,
        data_validade: PropTypes.string.isRequired,
        dias_para_vencer: PropTypes.number,
    })),
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

EstoqueAlertasTable.defaultProps = {
    alertas: [],
    className: '',
};

export default EstoqueAlertasTable;
